export type UpgradeGuideDecision = 'show-guide' | 'buy-card';

export interface UpgradeAvailability {
  available: boolean;
}

export interface UpgradePlan {
  tier_rank?: number | null;
  skus: UpgradeSku[];
}

export interface UpgradeSku {
  validity_period: string;
}

export interface UpgradeUser {
  membership?: boolean;
  pointInfo?: {
    tier_rank?: number | null;
    free_week_membership_expired?: boolean;
  };
}

const VALIDITY_PERIOD_MONTHS: Record<string, number> = {
  one_day: 0,
  one_week: 0,
  one_month: 1,
  two_months: 2,
  three_months: 3,
  six_months: 6,
  one_year: 12,
};

export function getUpgradeGuideDecision(
  feeRes: UpgradeAvailability | null,
): UpgradeGuideDecision {
  return feeRes?.available ? 'show-guide' : 'buy-card';
}

export function canUpgradeBySku(
  targetTierRank: number,
  targetPeriod: string,
  currentTierRank: number,
  currentPeriod?: string | null,
): boolean {
  if (!currentPeriod) return false;
  const targetMonths = VALIDITY_PERIOD_MONTHS[targetPeriod] ?? 0;
  const currentMonths = VALIDITY_PERIOD_MONTHS[currentPeriod] ?? 0;
  return targetTierRank > currentTierRank && targetMonths >= currentMonths;
}

export function hasCurrentPeriodUpgradeTarget(
  plan: UpgradePlan,
  user: UpgradeUser | null,
  currentMembershipPeriod?: string | null,
): boolean {
  const userTier = user?.pointInfo?.tier_rank ?? 0;
  const planTier = plan.tier_rank ?? 0;
  const isPaidMember = !!(
    user?.membership && user?.pointInfo?.free_week_membership_expired
  );

  if (!isPaidMember || !currentMembershipPeriod) {
    return false;
  }

  return plan.skus.some((sku) =>
    canUpgradeBySku(
      planTier,
      sku.validity_period,
      userTier,
      currentMembershipPeriod,
    ),
  );
}

export function resolveUpgradeSku<Sku extends UpgradeSku>(
  plan: { tier_rank?: number | null; skus: Sku[] },
  clickedSku: Sku,
  user: UpgradeUser | null,
  currentMembershipPeriod?: string | null,
): Sku {
  const userTier = user?.pointInfo?.tier_rank ?? 0;
  const planTier = plan.tier_rank ?? 0;
  const isPaidMember = !!(
    user?.membership && user?.pointInfo?.free_week_membership_expired
  );

  if (!isPaidMember || !currentMembershipPeriod) {
    return clickedSku;
  }

  if (planTier <= userTier) {
    return clickedSku;
  }

  if (
    canUpgradeBySku(
      planTier,
      clickedSku.validity_period,
      userTier,
      currentMembershipPeriod,
    )
  ) {
    return clickedSku;
  }

  return (
    plan.skus.find((sku) =>
      canUpgradeBySku(
        planTier,
        sku.validity_period,
        userTier,
        currentMembershipPeriod,
      ),
    ) ?? clickedSku
  );
}

export function resolveUpgradeGuideSkus<Sku extends UpgradeSku>(
  plan: { tier_rank?: number | null; skus: Sku[] },
  clickedSku: Sku,
  user: UpgradeUser | null,
  currentMembershipPeriod?: string | null,
): { upgradeSku: Sku; buySku: Sku } {
  return {
    upgradeSku: resolveUpgradeSku(
      plan,
      clickedSku,
      user,
      currentMembershipPeriod,
    ),
    buySku: clickedSku,
  };
}
